import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type { BrandAttributionValue } from '../../config/instance-settings-registry';
import type { StorefrontCatalogSortValue } from '../../config/storefront-catalog-sort.config';
import type { WebSettingsOverrideModel } from '../../forms/web-settings';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import { PortalSiteDomainEntity } from './portal-site-domain.entity';

/**
 * Mobile bottom-nav tab vocabulary for the storefront (PD-14609). The
 * portal admin picks up to four, in order; 'menu' is required (it is the
 * only path to the drawer, which carries sign-out, language, theme, and
 * the long-tail destinations).
 */
export const PORTAL_MOBILE_NAV_TABS = [
	'home',
	'shop',
	'account',
	'cart',
	'chat',
	'orders',
	'invoices',
	'menu',
] as const;
export type PortalMobileNavTab = (typeof PORTAL_MOBILE_NAV_TABS)[number];

/** Shipped default: Home / Shop / Account / Menu. */
export const DEFAULT_PORTAL_MOBILE_NAV_TABS: Array<PortalMobileNavTab> = [
	'home',
	'shop',
	'account',
	'menu',
];

/**
 * What the storefront's desktop header shows in its support slot (PD-15235).
 * One choice per storefront: nothing, a help icon opening the full Get Help
 * panel as a modal, or one piece of contact info inline. The `sales-rep-*`
 * options render only for signed-in customers (the rep is per active
 * account) and only when the site's showSalesRepInfo toggle allows it.
 * The `sales-*` options are the brand's GENERAL sales contact (PD-15552) —
 * site-level, not per-account, so they are public like the support options.
 * Default 'phone' — the header's historical behavior (PD-14363).
 */
export const PORTAL_HEADER_SUPPORT_DISPLAYS = [
	'none',
	'help-icon',
	'phone',
	'email',
	'business-hours',
	'sales-rep-name',
	'sales-rep-email',
	'sales-rep-phone',
	'sales-phone',
	'sales-email',
] as const;
export type PortalHeaderSupportDisplay =
	(typeof PORTAL_HEADER_SUPPORT_DISPLAYS)[number];

/**
 * Where the storefront's Size line gets its value (PD-16126):
 *   - `'sizeCountUom'` — the item's `sizeCount` plus `unitOfMeasure`
 *     ("500 Tablet"). The default.
 *   - `'cf_<fieldKey>'` — an item custom field, shown as stored ("500TB").
 * See `libs/shared/src/utils/catalog-item-size.ts` for the parsing and the
 * rule for which custom fields may be used.
 */
export type PortalCatalogItemSizeSource = 'sizeCountUom' | `cf_${string}`;

/**
 * Persisted shape of `portal_site.settings`. All fields optional — missing
 * keys take the resolver's hardcoded defaults (see resolvePortalSiteSettings).
 *
 * Stock is split into two independent toggles per visitor type:
 *   - **availability**: the In Stock / Out of Stock pill (boolean shape).
 *     Cheap to expose and rarely sensitive — defaults to true for auth.
 *   - **quantity**: the actual number on hand. Often considered competitively
 *     sensitive — defaults to false even for auth so admins opt in.
 *
 * Quantity should only be considered visible if availability is also on;
 * the resolver treats quantity-without-availability as effectively false.
 *
 * Legacy keys (`anonymousShowStock`, `showStockToAuthenticated`) are still
 * read for back-compat with rows written before the split — see
 * `resolveEffectivePortalSiteSettings` for the migration logic.
 */
export interface PortalSiteSettings {
	/** Allow unauthenticated visitors to browse the catalog at all. Default false. */
	allowAnonymousBrowsing?: boolean;
	/** Show price to unauthenticated visitors. Only meaningful when anonymous browsing is allowed. Default false. */
	anonymousShowPricing?: boolean;
	/** Show "In Stock / Out of Stock" availability to anonymous visitors. Default false. */
	anonymousShowStockAvailability?: boolean;
	/** Show numeric quantity on hand to anonymous visitors. Default false; only meaningful when availability is also on. */
	anonymousShowStockQuantity?: boolean;
	/** Show "In Stock / Out of Stock" availability to authenticated users. Default true. */
	showStockAvailabilityToAuthenticated?: boolean;
	/** Show numeric quantity on hand to authenticated users. Default false; only meaningful when availability is also on. */
	showStockQuantityToAuthenticated?: boolean;

	// --- Catalog product identifiers (PD-16126) ---
	// Extra lines on the catalog grid card, list row, and product detail page
	// (favorites share those surfaces). Both default false, so a storefront
	// looks exactly as before until an admin opts in. They apply to every
	// visitor who can see the catalog, signed in or not. The item number
	// ("#<id>") is not a setting: it always shows.
	/** Show the item NDC, formatted 5-4-2 with dashes. */
	showCatalogItemNdc?: boolean;
	/** Show a Size line, valued from `catalogItemSizeSource`. */
	showCatalogItemSize?: boolean;
	/**
	 * Where the Size line's value comes from. Default `'sizeCountUom'`. A
	 * custom-field source must name an active, unrestricted item text field;
	 * the portal-site save rejects anything else, and the catalog hides Size
	 * if the field later stops qualifying.
	 */
	catalogItemSizeSource?: PortalCatalogItemSizeSource;

	// --- Authenticated-experience toggles (catalog-only-mode storefronts) ---
	/**
	 * Allow authenticated users to browse the product catalog at all —
	 * the Shop nav link, the header search, the catalog/category routes,
	 * and the catalog-based dashboard widgets. Default true.
	 *
	 * Off = an "account-portal-only" storefront: the customer can view
	 * order history, view/pay invoices, and reach an item's detail page
	 * ONLY by clicking a line item from their own order/invoice — there is
	 * no browse or search entry point. Because there's no way to put an
	 * arbitrary item in a cart in this mode, the resolver also forces
	 * `allowPurchasing` to false when this is off (see
	 * resolveEffectivePortalSiteSettings); the item detail reached from an
	 * order/invoice line therefore renders with no add-to-cart, matching
	 * the existing browse-only gating.
	 */
	allowCatalogBrowsing?: boolean;
	/**
	 * Allow authenticated users to add to cart and place orders. Some
	 * customers want a "browse-only" portal where logged-in users can see
	 * prices/stock but cannot purchase (e.g. quote-driven sales). Default
	 * true. Always coerced to false when `allowCatalogBrowsing` is off.
	 */
	allowPurchasing?: boolean;
	/**
	 * Hide item pricing across the entire storefront CATALOG — the catalog
	 * grid/list, product detail, favorites, dashboard item widgets, and all
	 * promo pricing (badges, strikethrough prices, headlines) — for every
	 * visitor, authenticated or anonymous (PD-14775). Order / invoice /
	 * statement / transaction history keeps its amounts: those are records
	 * of real transactions, unlike the per-user `hidePricing` flag which
	 * also suppresses the financial surfaces.
	 *
	 * Only meaningful on a browse-only storefront: purchasing without
	 * visible prices is incoherent, so the resolver coerces this to false
	 * whenever `allowPurchasing` is on (and the editor disables the
	 * checkbox). When active it also coerces `anonymousShowPricing` off so
	 * guests can't see prices the customers themselves can't. Default false.
	 */
	hideCatalogPricing?: boolean;
	/** Allow authenticated users to view their order history. Default true. */
	allowOrderHistory?: boolean;
	/**
	 * Show the "Ship Via" row on the storefront order-detail page. Default
	 * true. Off for tenants whose integration populates `shipVia` with an
	 * internal value that isn't meaningful to a customer — e.g. a food
	 * distributor doing its own delivery, whose Fishbowl "carriers" are
	 * internal route/truck codes (PD-14771). Back-office (CRM) and any
	 * real-carrier tenant still show it.
	 */
	showOrderShipVia?: boolean;
	/** Allow authenticated users to view their invoices. Default true. */
	allowInvoiceViewing?: boolean;
	/**
	 * Allow authenticated users to pay invoices through the portal. Default
	 * true. Auto-coerced to false at runtime when no payment gateway is
	 * configured for the portal's company — the UI must render that case
	 * the same as the admin-disabled case.
	 */
	allowInvoicePayments?: boolean;
	/**
	 * Show the "Available Credit" metric (and its credit-limit sublabel) on
	 * the storefront account snapshot. Default true. Off for storefronts
	 * whose tenant doesn't manage per-customer credit limits in Workd, where
	 * the metric would only ever render an unhelpful "—" (PD-14899, HF).
	 */
	showAvailableCredit?: boolean;
	/**
	 * Show payment terms (e.g. "Net 30") to customers on the storefront —
	 * the invoice-detail Terms line and the statement-detail Payment Terms
	 * tile. Default true. Off for distributors who negotiate terms
	 * per-customer and treat them as internal, so the storefront must not
	 * surface them even though the CRM tracks them (PD-14898, HF).
	 */
	showPaymentTerms?: boolean;
	/**
	 * Allow authenticated users to view their statements. Default true.
	 * Independent of the invoices toggle so account-focused portals can
	 * shape exactly which financial surfaces appear (PD-14675).
	 */
	allowStatementViewing?: boolean;
	/** Allow authenticated users to view their transactions. Default true. */
	allowTransactionViewing?: boolean;
	/**
	 * Allow authenticated users to reach the customer-support chat (the
	 * floating launcher, the mobile Chat tab, and the chat conversation
	 * surface). Default FALSE — chat is opt-in per portal because rep
	 * routing/staffing has to exist on the tenant side before customers
	 * can be invited to start conversations (PD-14608).
	 */
	allowChat?: boolean;
	/**
	 * Ordered mobile bottom-nav tabs (max 4; 'menu' always enforced).
	 * Absent = the shipped default (home, shop, account, menu). Unknown
	 * keys are dropped and the list is sanitized at resolve time, so a
	 * hand-edited JSON blob can't break the nav (PD-14609).
	 */
	mobileNavTabs?: Array<PortalMobileNavTab>;
	/**
	 * Where authenticated users land after sign-in:
	 *   - `'dashboard'` — the storefront dashboard (default).
	 *   - `'browse'`    — straight into the catalog (requires catalog browsing).
	 *   - `'orders'`    — order history (requires order history).
	 *   - `'account'`   — the account page (always valid).
	 * Capability-validated by the resolver (`coerceAuthenticatedView`): a value
	 * whose prerequisite feature is off can never be served — it collapses to
	 * `'dashboard'`. Default `'dashboard'`.
	 */
	defaultAuthenticatedView?: 'browse' | 'dashboard' | 'orders' | 'account';
	/**
	 * What the storefront's sign-in asks for after the visitor enters their
	 * email address or phone number (PD-16471):
	 *   - `'email'` — "Password" in the editor: the password, with "Email/Text
	 *     me a sign-in code instead" one tap below. Default.
	 *   - `'phone'` — "Sign-in code" in the editor: a one-time code is sent
	 *     as soon as they continue, with "Use my password instead" below.
	 * The stored values predate the identifier-first sign-in, when they chose
	 * between an email form and a phone form; they are kept so no stored row
	 * changes meaning. A code goes by email for an email address; for a phone
	 * number it needs the tenant's storefront text sign-in, and without that
	 * the storefront shows the password instead.
	 */
	defaultLoginMethod?: 'email' | 'phone';

	// --- Legacy keys (back-compat for rows written before the avail/qty split) ---
	/** @deprecated use anonymousShowStockAvailability + anonymousShowStockQuantity */
	anonymousShowStock?: boolean;
	/** @deprecated use showStockAvailabilityToAuthenticated + showStockQuantityToAuthenticated */
	showStockToAuthenticated?: boolean;

	// --- Branding ---
	// LEGACY (PD-16557): brandAttribution, loginTheme and the support and
	// sales contacts below moved to BrandEntity. The API strips them from
	// settings on save and nothing reads them; PD-16559 clears the rest.
	/**
	 * Per-storefront override for the "Powered by X" attribution footer
	 * (PD-14113). Resolved into the public bootstrap with precedence over the
	 * tenant `branding.attribution` instance setting and the brand default;
	 * omit to inherit the tenant/brand value.
	 */
	brandAttribution?: BrandAttributionValue | null;
	/**
	 * Theme for the PRE-LOGIN surfaces only — the welcome/lockout screen
	 * (HomePage), the LoginModal, and (by asset choice) the native splash.
	 * `'dark'` (default) keeps the long-standing dark login identity: dark
	 * surface, white text, the dark-context `logoUrl`. `'light'` renders
	 * those surfaces light: light surface, dark text, the `logoUrlLight`
	 * variant. This is independent of the POST-login storefront theme, which
	 * the user toggles themselves from the profile menu (UserMenu). Default
	 * `'dark'`. Each brand sets its own preference. */
	loginTheme?: 'light' | 'dark';

	// --- Customer support contact (PD-14363) ---
	/**
	 * Customer-service phone number shown on the storefront's pre-login
	 * surfaces (welcome screen, login modal), the desktop header, and the
	 * mobile menu drawer. Display string (e.g. "1-800-555-0100") — rendered
	 * as a `tel:` link with non-dial characters stripped from the href.
	 */
	supportPhone?: string | null;
	/**
	 * Customer-service email shown alongside the phone on the login
	 * surfaces and mobile menu drawer. Rendered as a `mailto:` link.
	 */
	supportEmail?: string | null;
	/**
	 * Free-text business hours shown with the support contact info
	 * (e.g. "Mon-Fri 8:00 AM-5:00 PM CT").
	 */
	supportBusinessHours?: string | null;
	// --- General sales contact (PD-15552) ---
	/**
	 * The brand's GENERAL sales phone — the number a prospect or customer
	 * calls to reach sales, independent of whichever rep happens to be
	 * assigned to their account. Shown on the same storefront surfaces as
	 * the support contact. Display string (e.g. "810-559-0259") — rendered
	 * as a `tel:` link with non-dial characters stripped from the href.
	 */
	salesPhone?: string | null;
	/**
	 * The brand's general sales email (e.g. "sales@example.com"), shown
	 * alongside `salesPhone`. Rendered as a `mailto:` link.
	 */
	salesEmail?: string | null;
	// --- Mobile app (PD-16471) ---
	/**
	 * This storefront's app on the App Store, as the full store link (e.g.
	 * "https://apps.apple.com/us/app/capital-trading/id6788757788"). When set,
	 * welcome emails show the official "Download on the App Store" badge
	 * linking to it. Only an https apps.apple.com link is accepted — see
	 * normalizeAppStoreLink. Absent = no badge.
	 */
	appStoreUrl?: string | null;
	/**
	 * This storefront's app on Google Play, as the full store link (e.g.
	 * "https://play.google.com/store/apps/details?id=com.example"). Shown as
	 * the official "Get it on Google Play" badge; same rules as appStoreUrl.
	 */
	googlePlayUrl?: string | null;
	/**
	 * Whether the storefront's Get Help surfaces show the active account's
	 * assigned salesperson (name + email + phone) alongside the support
	 * contact info (PD-15235, phone added by PD-15552) — matching the 1.0
	 * shop's Get Help panel. Default TRUE (opted in); set false to keep
	 * internal rep contact details off this storefront.
	 */
	showSalesRepInfo?: boolean;
	/**
	 * What the desktop header's support slot shows (PD-15235) — see
	 * PORTAL_HEADER_SUPPORT_DISPLAYS. Default 'phone'.
	 */
	headerSupportDisplay?: PortalHeaderSupportDisplay;

	// --- Catalog faceted-filter exposure ---
	/**
	 * Which faceted-search filters this storefront exposes, and in what order.
	 * Each entry references an ItemFilterField by its stable `key` (a built-in
	 * facet like `manufacturer` / `price` / `isInStock`, or a custom field like
	 * `cf_allergen`). The ARRAY ORDER is the display order in the filter rail;
	 * presence = exposed, absence = hidden. `labelOverride` renames the facet for
	 * this site only (falling back to the registry / custom-field default label).
	 *
	 * Per-site only: when this is null/absent the storefront falls back to a
	 * hardcoded system default set. The catalog-config resolver further strips any
	 * field the current visitor can't see (ItemFilterField.gatedBy — e.g. price /
	 * in-stock when pricing / stock is hidden), so admin exposure is necessary but
	 * not sufficient.
	 */
	catalogFilters?: Array<PortalSiteCatalogFilterSetting>;
	/**
	 * The sort the storefront catalog opens with (PD-15272), one of the shared
	 * storefront sort values. Absent = the standard order (in-stock first where
	 * stock is shown, then by name). Applied only when the catalog is opened
	 * without a sort of its own, and dropped for a visitor whose storefront
	 * does not offer that sort (a price sort when pricing is hidden).
	 */
	catalogDefaultSort?: StorefrontCatalogSortValue;
	/**
	 * PD-16577 — this brand's override of the tenant's web settings (retention of web form
	 * submissions, Privacy Policy and Terms versions). Absent, or a value left out, means
	 * the tenant's value (Settings → Ecommerce → Web Settings); see resolveWebSettings.
	 */
	webSettings?: WebSettingsOverrideModel;
}

/**
 * One exposed faceted-search filter on a storefront. See
 * PortalSiteSettings.catalogFilters.
 */
export interface PortalSiteCatalogFilterSetting {
	/** ItemFilterField.key — 'manufacturer', 'price', 'isInStock', 'cf_allergen', … */
	key: string;
	/** Optional per-site display label; falls back to the field's default label. */
	labelOverride?: string;
	/**
	 * PD-15272: the storefront catalog opens with this filter already on.
	 * Honored only for yes/no (boolean) filters the visitor can see — an In
	 * Stock default is ignored for a visitor who is not shown stock. The
	 * shopper can turn it off; it is not applied again on that visit.
	 */
	onByDefault?: boolean;
}

/**
 * A `portal_site` is the storefront identity for a single company in a
 * tenant — 1:1 with `company`. There's no notion of a brand spanning
 * multiple companies; "brand" is purely UI sugar over a company's
 * storefront (alias for the URL, logo, email logo, theming).
 *
 * `isDefault` only means "this brand can be reached via the bare
 * <tenant>.my.workd.com URL too" — every brand still has its own alias
 * and the prefixed URL works in addition. There is no brand fallback
 * routing: an unmatched alias is a 404, full stop.
 */
@Index('UK_portal_site_alias', ['alias'], { unique: true })
@Index('UK_portal_site_company', ['fkCompanyId'], { unique: true })
@Entity('portal_site')
export class PortalSiteEntity extends BaseEntity {
	// Required for every brand. Lowercased [a-z0-9]+ (no dashes — dash is
	// the brand/tenant delimiter in the URL). Unique across the tenant.
	@Column('varchar', { name: 'alias', length: 32 })
	public alias!: string;

	// 1:1 with company. Unique across the tenant — exactly one storefront
	// per company. Default 0 so newly-synced rows aren't immediately
	// constraint-violating; service layer requires a real id on create.
	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: 0,
	})
	public fkCompanyId!: number;

	@Column('varchar', { name: 'displayName', length: 128 })
	public displayName!: string;

	@Column('tinyint', {
		name: 'isActive',
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean;

	// "Bare URL maps to this brand too." At most one row per tenant should
	// have isDefault = 1. The aliased URL still works on default rows; this
	// is purely a "no prefix needed" convenience for single-brand tenants
	// (and the tenant's chosen primary brand in multi-brand setups).
	@Column('tinyint', {
		name: 'isDefault',
		width: 1,
		default: () => "'0'",
	})
	public isDefault!: boolean;

	// LEGACY (PD-16557): branding moved to BrandEntity. Nothing reads or
	// writes this column any more; PD-16559 drops it.
	//
	// Brand wordmark for dark-mode / dark-surface contexts (the storefront
	// header is painted with primaryColor and stays dark in both modes, so
	// this is the "default" logo for that surface). Tenants typically upload
	// a white/light wordmark here. Falls back to itself in light mode if
	// `logoUrlLight` is unset, so existing single-asset tenants keep working.
	@Column('varchar', { name: 'logoUrl', nullable: true, length: 512 })
	public logoUrl!: string | null;

	// LEGACY (PD-16557): branding moved to BrandEntity. Nothing reads or
	// writes this column any more; PD-16559 drops it.
	//
	// Brand wordmark variant for light-mode surfaces — used post-login when
	// the tenant has opted into a light mode that paints surfaces white,
	// where a white-on-dark wordmark would disappear. Optional: when null,
	// consumers fall back to `logoUrl`.
	@Column('varchar', { name: 'logoUrlLight', nullable: true, length: 512 })
	public logoUrlLight!: string | null;

	// LEGACY (PD-16557): branding moved to BrandEntity. Nothing reads or
	// writes this column any more; PD-16559 drops it.
	@Column('varchar', { name: 'emailLogoUrl', nullable: true, length: 512 })
	public emailLogoUrl!: string | null;

	/**
	 * Storefront-scoped ecommerce settings. JSON column so we can evolve
	 * without a migration per knob. Fields are intentionally optional in
	 * the type so older rows (stored as `null`) and partial saves both
	 * work; the resolver applies hardcoded defaults for any missing key.
	 *
	 * The API NEVER trusts a client-provided "show price" flag — it always
	 * reads this column server-side after middleware resolves the active
	 * portal_site from the request hostname.
	 */
	@Column('json', { name: 'settings', nullable: true })
	public settings!: PortalSiteSettings | null;

	// LEGACY (PD-16557): branding moved to BrandEntity. Nothing reads or
	// writes this column any more; PD-16559 drops it.
	//
	// Hero image rendered behind the storefront LoginModal (right-side brand
	// panel of the split-pane sign-in screen, full-bleed background on
	// mobile/Capacitor). CDN URL produced by PublicCdnUploadService.
	@Column('varchar', {
		name: 'loginBackgroundUrl',
		nullable: true,
		length: 512,
	})
	public loginBackgroundUrl!: string | null;

	// LEGACY (PD-16557): branding moved to BrandEntity. Nothing reads or
	// writes this column any more; PD-16559 drops it.
	//
	// Storefront favicon. Swapped into <head> on tenant resolution so the
	// browser tab shows the brand mark instead of the generic Workd icon.
	@Column('varchar', { name: 'faviconUrl', nullable: true, length: 512 })
	public faviconUrl!: string | null;

	// LEGACY (PD-16557): the three palette columns below moved to
	// BrandEntity. Nothing reads or writes them any more; PD-16559 drops them.
	//
	// PD-16045 — the brand palette is a primary and an accent per mode; every
	// surface is a fixed Workd neutral. The five override columns this entity
	// used to declare (primaryColorLight, pageBackgroundColor and its light
	// variant, surfaceBackgroundColor and its light variant,
	// loginBackgroundColor) are no longer read; the columns stay in the
	// schema, nulled by the SetStorefrontBrandPalette migration, until a
	// later cleanup drops them.
	//
	// Brand primary as a CSS hex string (e.g. '#015350'): the header and
	// bottom nav in light mode and the pre-login backdrop in both modes.
	@Column('varchar', { name: 'primaryColor', nullable: true, length: 16 })
	public primaryColor!: string | null;

	// Brand accent for DARK mode as a CSS hex string: buttons, links, the
	// active tab and the focus ring while the header is neutral navy.
	@Column('varchar', { name: 'accentColor', nullable: true, length: 16 })
	public accentColor!: string | null;

	// Brand accent for LIGHT mode. When null the storefront borrows the
	// dark-mode accent, then the standard palette.
	@Column('varchar', {
		name: 'accentColorLight',
		nullable: true,
		length: 16,
	})
	public accentColorLight!: string | null;

	// Master switch for self-serve customer registration on this portal.
	// Off by default — leaving a public write endpoint open on a tenant
	// who hasn't asked for it is a footgun. When false, the public
	// /portal/register endpoint refuses submissions for this brand AND the
	// "Register" link in the storefront login UI is hidden.
	@Column('tinyint', {
		name: 'isRegistrationEnabled',
		width: 1,
		default: () => "'0'",
	})
	public isRegistrationEnabled!: boolean;

	// Per-portal override for the email recipient who gets notified when a
	// new lead lands via /portal/register. Falls back to the tenant-level
	// `notifications.lead_registration_email` instance setting when null,
	// then to a system default if that is also unset.
	@Column('varchar', {
		name: 'leadNotificationEmail',
		nullable: true,
		length: 255,
	})
	public leadNotificationEmail!: string | null;

	// JSON array of fully-qualified origin strings (scheme + host + optional
	// port — e.g. ["https://shop.example.com"]) that may POST to the public
	// /portal/register endpoint on behalf of this brand from outside our
	// own subdomain. Tenants register their own marketing site origin here
	// so they can host a branded form that submits cross-origin. Our own
	// portal subdomain is auto-trusted server-side and need not be listed.
	// Null/empty means "only our own subdomain may submit."
	@Column('json', { name: 'registrationAllowedOrigins', nullable: true })
	public registrationAllowedOrigins!: Array<string> | null;

	// LEGACY (PD-16557): the three sender columns below moved to BrandEntity.
	// Nothing reads or writes them any more; PD-16559 drops them.
	//
	// Per-portal outbound sender override. When set, storefront email for
	// this brand is sent from `emailFromLocalPart@emailFromDomain` instead
	// of the tenant-wide default resolved from the primary sending domain.
	// The domain must be one of the tenant's verified sending domains
	// (validated server-side on save) — an unverified domain is rejected by
	// the vendor at send time, so we refuse it up front. Null on any of the
	// three means "fall back to the tenant default".
	@Column('varchar', {
		name: 'emailFromDomain',
		nullable: true,
		length: 150,
	})
	public emailFromDomain!: string | null;

	@Column('varchar', {
		name: 'emailFromLocalPart',
		nullable: true,
		length: 64,
	})
	public emailFromLocalPart!: string | null;

	// Display name shown to recipients. Independent of the address pair —
	// a brand may want its own display name on the tenant default address.
	@Column('varchar', {
		name: 'emailFromName',
		nullable: true,
		length: 150,
	})
	public emailFromName!: string | null;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@OneToMany(
		() => PortalSiteDomainEntity,
		(portalSiteDomain) => portalSiteDomain.fkPortalSite,
	)
	public portalSiteDomains!: Array<PortalSiteDomainEntity>;
}
